/**
 * 节点的复制 / 粘贴 —— 纯逻辑，零 Vue 依赖。
 *
 * 两个容易做错的地方：
 *
 * 1. **只带内部连线。** 只复制节点的话，粘贴出来是一堆孤立的节点；
 *    但如果把跨越选区内外的边也带上，粘贴出来的节点会连回原图 ——
 *    那是用户没预期的。所以只保留**两端都在选区内**的边。
 * 2. **id 必须重分配。** 而且我们节点数据里**也存了一份 id**
 *    （`data.model.id` 和 `node.id` 是同一个值），只改外面会让模型和节点对不上，
 *    后续按 id 查模型全都失效。所以留了 `rewriteData` 钩子。
 */
import { edgeIdOf } from './edgeOps'

interface LooseNode {
  id: string
  position?: { x: number; y: number }
  [key: string]: unknown
}

interface LooseEdge {
  id?: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
  [key: string]: unknown
}

export interface ClipboardPayload {
  nodes: Record<string, unknown>[]
  edges: Record<string, unknown>[]
}

/**
 * 复制选中的节点。
 *
 * 边的保留条件是「两端都在选区内」—— 跨越选区边界的边不带，
 * 因为粘贴后它指向的节点并不存在。
 */
export function copyNodes(
  nodes: readonly unknown[],
  edges: readonly unknown[],
  selectedIds: readonly string[],
): ClipboardPayload {
  const picked = new Set(selectedIds)
  if (picked.size === 0) return { nodes: [], edges: [] }

  const copiedNodes = (nodes as LooseNode[])
    .filter((n) => picked.has(n.id))
    .map((n) => JSON.parse(JSON.stringify(n)) as Record<string, unknown>)

  const copiedEdges = (edges as LooseEdge[])
    .filter((e) => picked.has(e.source) && picked.has(e.target))
    .map((e) => JSON.parse(JSON.stringify(e)) as Record<string, unknown>)

  return { nodes: copiedNodes, edges: copiedEdges }
}

export interface PasteOptions {
  /**
   * 给新节点分配 id。
   *
   * 注入而不是内部生成：这样「同一份剪贴板粘三次」在测试里是可以预期的。
   */
  makeId(originalId: string, index: number): string
  /** 整体位移，避免粘贴出来的东西正好盖住原件 */
  offset: { x: number; y: number }
  /**
   * 改写节点数据里那份 id。
   *
   * 我们的 `data.model.id` 和 `node.id` 必须保持一致，
   * 只改外面会让「按 id 查模型」全部失效。
   */
  rewriteData?(data: unknown, newId: string, oldId: string): unknown
}

export interface PasteResult {
  nodes: Record<string, unknown>[]
  edges: Record<string, unknown>[]
  /** 旧 id → 新 id，调用方用它做后续选中 */
  idMap: Map<string, string>
}

export function pasteNodes(
  payload: ClipboardPayload,
  options: PasteOptions,
): PasteResult {
  const idMap = new Map<string, string>()

  payload.nodes.forEach((node, index) => {
    const oldId = String((node as LooseNode).id ?? '')
    if (oldId) idMap.set(oldId, options.makeId(oldId, index))
  })

  const nodes = payload.nodes.map((node) => {
    const source = node as LooseNode
    const newId = idMap.get(String(source.id)) ?? source.id
    const next: Record<string, unknown> = {
      ...JSON.parse(JSON.stringify(source)),
      id: newId,
    }

    const position = source.position ?? { x: 0, y: 0 }
    next.position = {
      x: position.x + options.offset.x,
      y: position.y + options.offset.y,
    }

    if (options.rewriteData && 'data' in next) {
      next.data = options.rewriteData(next.data, newId, String(source.id))
    }

    return next
  })

  const edges = payload.edges.map((edge) => {
    const source = edge as LooseEdge
    const newSource = idMap.get(source.source) ?? source.source
    const newTarget = idMap.get(source.target) ?? source.target
    return {
      ...JSON.parse(JSON.stringify(source)),
      /* 边 id 也重算（否则和原图撞 id）—— 约定只从 `edgeOps` 来 */
      id: edgeIdOf(newSource, source.sourceHandle, newTarget, source.targetHandle),
      source: newSource,
      target: newTarget,
    } as Record<string, unknown>
  })

  return { nodes, edges, idMap }
}
